import type { Katalog, KatalogAktion, KatalogQuelle, Platzhalter } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router";
import { schluessel, useKatalog, useStruktur } from "../api/abfragen.ts";
import { api, ApiFehler } from "../api/klient.ts";
import { useDarf } from "../huelle/konto.ts";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./seiten.css";
import "./katalog.css";

type Speichern = (teil: Partial<Katalog>) => Promise<boolean>;

function Bereich({ titel, zahl, aktion, children }: { titel: string; zahl?: number; aktion?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <section className="bereich" aria-labelledby={id}>
      <header className="bereich-kopf">
        <h2 id={id}>
          {titel}
          {zahl !== undefined && <span className="leise zahlen"> {zahl}</span>}
        </h2>
        {aktion}
      </header>
      {children}
    </section>
  );
}

function argumentText(a: KatalogAktion["argument"]): string {
  if (a === "keins") return "–";
  if (a === "frei") return "Freier Wert";
  if (a === "ui") return "UI-Schlüssel";
  return `Werte: ${a.werte.join(", ")}`;
}

function AktionFormular({ alt, vergeben, onSpeichern, onAbbrechen }: { alt: KatalogAktion | null; vergeben: Set<string>; onSpeichern: (a: KatalogAktion) => void; onAbbrechen: () => void }) {
  const id = useId();
  const [schl, setSchl] = useState(alt?.schluessel ?? "");
  const [name, setName] = useState(alt?.name ?? "");
  const [beschreibung, setBeschreibung] = useState(alt?.beschreibung ?? "");
  const [art, setArt] = useState<"keins" | "frei" | "ui" | "werte">(alt ? (typeof alt.argument === "string" ? alt.argument : "werte") : "keins");
  const [werte, setWerte] = useState(alt && typeof alt.argument === "object" ? alt.argument.werte.join(", ") : "");
  const fehler = !schl.trim() ? "Schlüssel fehlt." : !alt && vergeben.has(schl.trim()) ? "Diesen Schlüssel gibt es schon." : !name.trim() ? "Anzeigename fehlt." : null;

  const absenden = (e: FormEvent) => {
    e.preventDefault();
    if (fehler) return;
    const liste = werte.split(",").map((w) => w.trim()).filter(Boolean);
    onSpeichern({
      schluessel: schl.trim(),
      name: name.trim(),
      ...(beschreibung.trim() ? { beschreibung: beschreibung.trim() } : {}),
      argument: art === "werte" ? { werte: liste } : art,
    });
  };

  return (
    <form className="katalog-formular" onSubmit={absenden}>
      <div className="feld">
        <label htmlFor={`${id}-s`}>Schlüssel</label>
        <input id={`${id}-s`} className="eingabe mono" value={schl} disabled={!!alt} placeholder="z. B. upgrade.kaufen" onChange={(e) => setSchl(e.target.value)} autoFocus={!alt} />
      </div>
      <div className="feld">
        <label htmlFor={`${id}-n`}>Anzeigename</label>
        <input id={`${id}-n`} className="eingabe" value={name} onChange={(e) => setName(e.target.value)} autoFocus={!!alt} />
      </div>
      <div className="feld katalog-formular-breit">
        <label htmlFor={`${id}-b`}>Beschreibung</label>
        <input id={`${id}-b`} className="eingabe" value={beschreibung} placeholder="Was passiert beim Klick?" onChange={(e) => setBeschreibung(e.target.value)} />
      </div>
      <div className="feld">
        <label htmlFor={`${id}-a`}>Argument</label>
        <select id={`${id}-a`} className="eingabe" value={art} onChange={(e) => setArt(e.target.value as typeof art)}>
          <option value="keins">Keins</option>
          <option value="frei">Freier Wert</option>
          <option value="ui">UI-Schlüssel</option>
          <option value="werte">Feste Werte</option>
        </select>
      </div>
      {art === "werte" && (
        <div className="feld">
          <label htmlFor={`${id}-w`}>Werte, durch Komma getrennt</label>
          <input id={`${id}-w`} className="eingabe mono" value={werte} onChange={(e) => setWerte(e.target.value)} />
        </div>
      )}
      <div className="katalog-formular-fuss">
        {fehler && (schl || name) ? <span className="feld-fehler">{fehler}</span> : <span />}
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf-primaer" disabled={!!fehler}>
            Speichern
          </button>
        </div>
      </div>
    </form>
  );
}

function Aktionen({ katalog, darf, speichern }: { katalog: Katalog; darf: boolean; speichern: Speichern }) {
  const [bearbeitet, setBearbeitet] = useState<string | "neu" | null>(null);
  const vergeben = new Set(katalog.aktionen.map((a) => a.schluessel));
  const sichern = async (a: KatalogAktion) => {
    const liste = bearbeitet === "neu" ? [...katalog.aktionen, a] : katalog.aktionen.map((x) => (x.schluessel === a.schluessel ? a : x));
    if (await speichern({ aktionen: liste })) setBearbeitet(null);
  };
  return (
    <Bereich
      titel="Aktionen"
      zahl={katalog.aktionen.length}
      aktion={
        darf &&
        bearbeitet === null && (
          <button type="button" className="knopf" onClick={() => setBearbeitet("neu")}>
            <Plus size={14} aria-hidden="true" /> Aktion anlegen
          </button>
        )
      }
    >
      <table className="tabelle">
        <thead>
          <tr>
            <th>Schlüssel</th>
            <th>Name</th>
            <th>Argument</th>
            <th>Beschreibung</th>
            {darf && <th aria-label="Aktion" />}
          </tr>
        </thead>
        <tbody>
          {katalog.aktionen.map((a) =>
            bearbeitet === a.schluessel ? (
              <tr key={a.schluessel}>
                <td colSpan={5}>
                  <AktionFormular alt={a} vergeben={vergeben} onSpeichern={(x) => void sichern(x)} onAbbrechen={() => setBearbeitet(null)} />
                </td>
              </tr>
            ) : (
              <tr key={a.schluessel}>
                <td className="mono">{a.schluessel}</td>
                <td>{a.name}</td>
                <td>{argumentText(a.argument)}</td>
                <td className="leise">{a.beschreibung}</td>
                {darf && (
                  <td className="tabelle-aktion">
                    <button type="button" className="knopf knopf-still knopf-symbol" aria-label={`${a.name} bearbeiten`} title="Bearbeiten" onClick={() => setBearbeitet(a.schluessel)}>
                      <Pencil size={13} aria-hidden="true" />
                    </button>
                    {a.schluessel !== "menue.oeffnen" && (
                      <button
                        type="button"
                        className="knopf knopf-still knopf-symbol"
                        aria-label={`${a.name} entfernen`}
                        title="Entfernen"
                        onClick={() => void speichern({ aktionen: katalog.aktionen.filter((x) => x.schluessel !== a.schluessel) })}
                      >
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ),
          )}
        </tbody>
      </table>
      {bearbeitet === "neu" && <AktionFormular alt={null} vergeben={vergeben} onSpeichern={(x) => void sichern(x)} onAbbrechen={() => setBearbeitet(null)} />}
    </Bereich>
  );
}

function Platzhalterliste({ titel, liste }: { titel: string; liste: Platzhalter[] }) {
  return (
    <div className="quelle-zeile">
      <dt>{titel}</dt>
      <dd>
        {liste.length === 0 ? (
          <span className="leise">–</span>
        ) : (
          <ul className="chips">
            {liste.map((p) => (
              <li key={p.name} className="chip mono" title={[p.beschreibung, `Beispiel: ${Array.isArray(p.beispiel) ? p.beispiel.join(" / ") : p.beispiel}`].filter(Boolean).join("\n")}>
                {`{${p.name}}`}
                {p.mehrzeilig && <span className="chip-zusatz">mehrzeilig</span>}
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}

function Zustaende({ titel, liste }: { titel: string; liste: string[] }) {
  return (
    <div className="quelle-zeile">
      <dt>{titel}</dt>
      <dd>
        {liste.length === 0 ? (
          <span className="leise">–</span>
        ) : (
          <ul className="chips">
            {liste.map((z) => (
              <li key={z} className="chip">
                {z}
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}

function Quelle({ q }: { q: KatalogQuelle }) {
  return (
    <article className="quelle">
      <h3>
        {q.name} <span className="mono leise">{q.schluessel}</span>
      </h3>
      <dl>
        <Platzhalterliste titel="Menü-Werte" liste={q.menueWerte} />
        <Zustaende titel="Menü-Zustände" liste={q.menueZustaende} />
        <Platzhalterliste titel="Eintrags-Werte" liste={q.eintragWerte} />
        <Zustaende titel="Eintrags-Zustände" liste={q.eintragZustaende} />
        <div className="quelle-zeile">
          <dt>Beispiel</dt>
          <dd className="zahlen">
            {q.beispiel.eintraege.length} {q.beispiel.eintraege.length === 1 ? "Eintrag" : "Einträge"}
          </dd>
        </div>
      </dl>
    </article>
  );
}

function Farbrollen({ katalog, darf, speichern }: { katalog: Katalog; darf: boolean; speichern: Speichern }) {
  const id = useId();
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#F5B841");
  const rollen = Object.entries(katalog.farbrollen);
  const gueltig = /^[a-z0-9_]+$/.test(name) && /^#[0-9A-Fa-f]{6}$/.test(hex);
  return (
    <Bereich titel="Farbrollen" zahl={rollen.length}>
      {rollen.length === 0 && <p className="leise">Noch keine Farbrollen. Texte nutzen sie als Tag, etwa &lt;primaer&gt;.</p>}
      {rollen.length > 0 && (
        <ul className="farbrollen">
          {rollen.map(([n, h]) => (
            <li key={n}>
              <span className="farbfleck" style={{ background: h }} aria-hidden="true" />
              <span className="mono">{`<${n}>`}</span>
              <span className="mono leise">{h.toUpperCase()}</span>
              {darf && (
                <button
                  type="button"
                  className="knopf knopf-still knopf-symbol"
                  aria-label={`Farbrolle ${n} entfernen`}
                  onClick={() => {
                    const neu = { ...katalog.farbrollen };
                    delete neu[n];
                    void speichern({ farbrollen: neu });
                  }}
                >
                  <Trash2 size={13} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {darf && (
        <form
          className="formreihe"
          onSubmit={async (e) => {
            e.preventDefault();
            if (gueltig && (await speichern({ farbrollen: { ...katalog.farbrollen, [name]: hex.toUpperCase() } }))) setName("");
          }}
        >
          <div className="feld">
            <label htmlFor={`${id}-n`}>Neue Rolle</label>
            <input id={`${id}-n`} className="eingabe mono" value={name} placeholder="primaer" onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="feld">
            <label htmlFor={`${id}-h`}>Farbe</label>
            <span className="farbe-eingabe">
              <input type="color" aria-label="Farbe wählen" value={/^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : "#000000"} onChange={(e) => setHex(e.target.value.toUpperCase())} />
              <input id={`${id}-h`} className="eingabe mono" value={hex} onChange={(e) => setHex(e.target.value)} />
            </span>
          </div>
          <button type="submit" className="knopf" disabled={!gueltig}>
            Hinzufügen
          </button>
        </form>
      )}
    </Bereich>
  );
}

function Darstellung({ katalog, darf, speichern }: { katalog: Katalog; darf: boolean; speichern: Speichern }) {
  const id = useId();
  const d = katalog.darstellung;
  return (
    <Bereich titel="Darstellung">
      <div className="feldgruppe katalog-schmal">
        <div className="feld">
          <label htmlFor={`${id}-t`}>Titelvorlage für Java</label>
          <input
            id={`${id}-t`}
            key={d.titelVorlage}
            className="eingabe mono"
            defaultValue={d.titelVorlage}
            disabled={!darf}
            onBlur={(e) => e.target.value !== d.titelVorlage && void speichern({ darstellung: { ...d, titelVorlage: e.target.value } })}
          />
          <span className="feld-hinweis">{"{titel}"} steht für den Titel der UI.</span>
        </div>
        <label className="schalter">
          <input type="checkbox" checked={d.kursivAus} disabled={!darf} onChange={(e) => void speichern({ darstellung: { ...d, kursivAus: e.target.checked } })} />
          <span>Kursiv in Namen und Beschreibungen standardmäßig aus</span>
        </label>
      </div>
    </Bereich>
  );
}

function JsonAnsicht({ katalog, darf, speichern }: { katalog: Katalog; darf: boolean; speichern: Speichern }) {
  const id = useId();
  const original = JSON.stringify(katalog, null, 2);
  const [text, setText] = useState(original);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => setText(original), [original]);
  const uebernehmen = async () => {
    let daten: Katalog;
    try {
      daten = JSON.parse(text) as Katalog;
    } catch (e) {
      setFehler(`Kein gültiges JSON: ${(e as Error).message}`);
      return;
    }
    setFehler(null);
    if (!(await speichern(daten))) setFehler("Der Server hat den Katalog abgelehnt; Details stehen in der Meldung.");
  };
  return (
    <section className="bereich json-ansicht">
      <label htmlFor={id} className="unsichtbar">
        Katalog als JSON
      </label>
      <textarea id={id} className="eingabe mono" spellCheck={false} value={text} readOnly={!darf} onChange={(e) => setText(e.target.value)} />
      {fehler && <p className="feld-fehler">{fehler}</p>}
      {darf && (
        <div className="knopfreihe">
          <button type="button" className="knopf" disabled={text === original} onClick={() => setText(original)}>
            Verwerfen
          </button>
          <button type="button" className="knopf knopf-primaer" disabled={text === original} onClick={() => void uebernehmen()}>
            Übernehmen
          </button>
        </div>
      )}
    </section>
  );
}

export function KatalogSeite() {
  const p = useParams();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darf = useDarf("katalog");
  const struktur = useStruktur();
  const plugin = struktur.data?.plugins.find((x) => x.schluessel === p.plugin);
  const katalog = useKatalog(plugin?.id);
  const [ansicht, setAnsicht] = useState<"uebersicht" | "json">("uebersicht");

  if (struktur.data && !plugin) {
    return (
      <main className="seite">
        <h1>Dieses Plugin existiert nicht.</h1>
      </main>
    );
  }
  if (!plugin || !katalog.data) return <main className="seite" aria-busy="true" />;
  const k = katalog.data;

  const speichern: Speichern = async (teil) => {
    try {
      const neu = await api<Katalog>(`/api/plugins/${plugin.id}/katalog`, { methode: "PUT", body: teil });
      qc.setQueryData(schluessel.katalog(plugin.id), neu);
      return true;
    } catch (e) {
      const details = e instanceof ApiFehler && Array.isArray(e.details) ? (e.details as { path?: unknown[]; message?: string }[]).slice(0, 3).map((d) => `${(d.path ?? []).join(".")}: ${d.message}`).join("; ") : "";
      hinweis({ text: `${(e as Error).message}${details ? ` ${details}` : ""}`, art: "fehler" });
      return false;
    }
  };

  return (
    <main className="seite seite-breit">
      <header className="seite-kopf seite-kopf-zeile">
        <div>
          <h1>Katalog {plugin.name}</h1>
          <p className="leise">Aktionen, Datenquellen und Farben, die der Editor für dieses Plugin kennt.</p>
        </div>
        <div className="umschalter" role="group" aria-label="Ansicht">
          <button type="button" aria-pressed={ansicht === "uebersicht"} onClick={() => setAnsicht("uebersicht")}>
            Übersicht
          </button>
          <button type="button" aria-pressed={ansicht === "json"} onClick={() => setAnsicht("json")}>
            JSON
          </button>
        </div>
      </header>
      {ansicht === "json" ? (
        <JsonAnsicht katalog={k} darf={darf} speichern={speichern} />
      ) : (
        <>
          <Aktionen katalog={k} darf={darf} speichern={speichern} />
          <Bereich titel="Datenquellen" zahl={k.quellen.length}>
            {k.quellen.length === 0 ? <p className="leise">Noch keine Datenquellen. Claude liest sie aus dem Plugin-Code; von Hand geht es über die JSON-Ansicht.</p> : k.quellen.map((q) => <Quelle key={q.schluessel} q={q} />)}
          </Bereich>
          <Farbrollen katalog={k} darf={darf} speichern={speichern} />
          <Darstellung katalog={k} darf={darf} speichern={speichern} />
          <Bereich titel="Bedrock-Bilder" zahl={Object.keys(k.bedrockBilder).length}>
            {Object.keys(k.bedrockBilder).length === 0 ? (
              <p className="leise">Keine Zuordnung. Bedrock-Knöpfe zeigen dann kein Bild.</p>
            ) : (
              <table className="tabelle">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Bedrock-Textur</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(k.bedrockBilder).map(([item, pfad]) => (
                    <tr key={item}>
                      <td className="mono">{item}</td>
                      <td className="mono leise">{pfad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Bereich>
        </>
      )}
    </main>
  );
}
